export const DEPLOYMENT_UPDATE_CONFIG_ENDPOINT = "/api/deployment-update/config";
export const DEPLOYMENT_UPDATE_BANNER_SETTING_KEY = "deployment_update_banner_enabled";

let config = { bannerEnabled: true, autoReloadRequired: true, initialized: false };
let revision = 0;
const listeners = new Set();

export function readDeploymentUpdateBannerEnabled() {
  return config.bannerEnabled;
}

export function readDeploymentUpdateConfig() {
  return config;
}

function updateConfig(updates) {
  const next = { ...config, ...updates };
  if (Object.keys(next).every((key) => next[key] === config[key])) return;
  config = next;
  listeners.forEach((listener) => listener());
}

export function subscribeDeploymentUpdateConfig(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function publishDeploymentUpdateBannerEnabled(value) {
  if (typeof value !== "boolean") return;
  revision += 1;
  updateConfig({ bannerEnabled: value });
}

export function shouldShowDeploymentUpdateGuard(phase, enabled) {
  return phase !== "monitoring" && (enabled || phase === "delayed" || phase === "manual");
}

// Continue à relire le réglage même quand le moniteur local est arrêté.
export function watchDeploymentUpdateConfig({
  fetchImpl = globalThis.fetch.bind(globalThis),
  windowRef = window,
  documentRef = document,
} = {}) {
  let stopped = false;
  let pendingRequest = null;
  const markInitialized = () => {
    if (!stopped) updateConfig({ initialized: true });
  };
  const startupTimeout = windowRef.setTimeout(markInitialized, 5000);

  const refresh = async () => {
    if (stopped || pendingRequest || documentRef.visibilityState === "hidden") return;
    const requestRevision = revision;
    const controller = new AbortController();
    pendingRequest = controller;
    const timeout = windowRef.setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetchImpl(DEPLOYMENT_UPDATE_CONFIG_ENDPOINT, {
        cache: "no-store",
        signal: controller.signal,
      });
      if (!response.ok) return;
      const payload = await response.json();
      if (!stopped && !controller.signal.aborted && typeof payload?.[DEPLOYMENT_UPDATE_BANNER_SETTING_KEY] === "boolean") {
        // Une sauvegarde concurrente prime ; le serveur décide de la politique.
        updateConfig({
          bannerEnabled: requestRevision === revision ? payload[DEPLOYMENT_UPDATE_BANNER_SETTING_KEY] : config.bannerEnabled,
          autoReloadRequired: payload.auto_reload_required !== false,
        });
      }
    } catch {
      // Conserve le réglage connu ; sans réponse, le rechargement reste requis par défaut.
    } finally {
      windowRef.clearTimeout(timeout);
      pendingRequest = null;
      markInitialized();
    }
  };

  windowRef.addEventListener("focus", refresh);
  windowRef.addEventListener("online", refresh);
  documentRef.addEventListener("visibilitychange", refresh);
  const interval = windowRef.setInterval(refresh, 60000);
  void refresh();
  return () => {
    stopped = true;
    windowRef.clearTimeout(startupTimeout);
    pendingRequest?.abort();
    windowRef.clearInterval(interval);
    windowRef.removeEventListener("focus", refresh);
    windowRef.removeEventListener("online", refresh);
    documentRef.removeEventListener("visibilitychange", refresh);
  };
}
